import * as React from "react";
import { useColorScheme } from "@mui/joy/styles";
import IconButton, { IconButtonProps } from "@mui/joy/IconButton";
import { useModeStore } from "../../stores/store-settings";

/**
 * Color Scheme Toggle Component
 * 
 * A button component that toggles between light and dark color schemes.
 * It integrates with both Material-UI's theme system and the application's custom theme store.
 * 
 * Features:
 * - Toggles between light and dark modes
 * - Persists theme preference
 * - Handles client-side hydration
 * - Supports custom styling and click handlers
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Function} props.onClick - Optional click handler
 * @param {Object} props.sx - Optional custom styles
 * @param {IconButtonProps} props - Additional Material-UI IconButton props
 * 
 * @example
 * ```tsx
 * <ColorSchemeToggle />
 * ```
 */
export default function ColorSchemeToggle({
  onClick,
  sx,
  ...props
}: IconButtonProps) {
  const { mode, setMode } = useColorScheme();
  const colorMode = useModeStore((store) => store.toggleMode);
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => {
    setMounted(true);
  }, []);
  if (!mounted) {
    return (
      <IconButton
        size="sm"
        variant="outlined"
        color="neutral"
        {...props}
        sx={sx}
        disabled
      />
    );
  }
  return (
    <IconButton
      id="toggle-mode"
      size="sm"
      variant="outlined"
      color="neutral"
      {...props}
      onClick={(event) => {
        if (mode === "light") {
          setMode("dark");
          colorMode();
        } else {
          setMode("light");
          colorMode();
        }
        onClick?.(event);
      }}
      sx={[
        {
          "& > *:first-of-type": {
            display: mode === "dark" ? "none" : "initial",
          },
          "& > *:last-of-type": {
            display: mode === "light" ? "none" : "initial",
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}>
      <i data-feather="moon" />
      <i data-feather="sun" />
    </IconButton>
  );
}
